import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type {
  DiagramInfo,
  DiagramViewDefinition,
} from '../../../hooks/useDiagramInfo';
import {
  DEFAULT_POSITION_CHART_GROUPING_POLICY,
  POSITION_CHART_GROUPING_FILTER_KEY,
  resolvePositionChartGroupingPolicy,
  type PositionChartGroupingPolicy,
} from '../models/PositionChartGrouping';

export function usePositionChartGrouping(
  folderId: string | null | undefined,
  diagramId: string | null | undefined,
  viewDefinition: DiagramViewDefinition | null | undefined,
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  // Parsing clones persisted policies. Keep that value stable so projection
  // effects do not reload the scene in response to their own store updates.
  const policy = useMemo(
    () => resolvePositionChartGroupingPolicy(viewDefinition?.filters),
    [viewDefinition?.filters],
  );
  const mutation = useMutation({
    mutationFn: async (policy: PositionChartGroupingPolicy) => {
      if (!folderId || !diagramId) {
        throw new Error('Position chart diagram is unavailable.');
      }
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/diagrams/${encodeURIComponent(diagramId)}/atlas/position-chart-grouping`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(policy),
        },
      );
      if (!response.ok) {
        throw new Error(`Position chart grouping update failed (${response.status})`);
      }
      return response.json() as Promise<{ policy: PositionChartGroupingPolicy }>;
    },
    onSuccess: ({ policy }) => {
      queryClient.setQueryData<DiagramInfo>(
        ['diagram', diagramId, 'info'],
        (current) => current ? {
          ...current,
          viewDefinition: {
            ...(current.viewDefinition ?? { type: 'position-chart', projection: 'position_chart' }),
            filters: {
              ...current.viewDefinition?.filters,
              [POSITION_CHART_GROUPING_FILTER_KEY]: policy,
            },
          },
        } : current,
      );
      void queryClient.invalidateQueries({
        queryKey: ['diagram', diagramId, 'info'],
      });
    },
  });

  return {
    policy: policy ?? DEFAULT_POSITION_CHART_GROUPING_POLICY,
    isLoading: false,
    error: null,
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
